import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GamePlayer } from '~/components/game/GamePlayer';
import { AuthForms } from '~/components/landing/AuthForms';
import { ResetPasswordForm } from '~/components/landing/ResetPasswordForm';
import { EpisodeList } from '~/components/landing/EpisodeList';
import { LandingView } from '~/components/landing/LandingView';
import { SeasonSelector } from '~/components/landing/SeasonSelector';
import { OfferPage } from '~/components/offer/OfferPage';
import { BrandedTransitionScreen } from '~/components/ui/BrandedTransitionScreen';
import { ErrorState } from '~/components/ui/ErrorState';
import { YakSplashScreen } from '~/components/ui/YakSplashScreen';
import { AppHeader } from '~/components/layout/AppHeader';
import { AuthProvider } from '~/contexts/AuthContext';
import { HeaderProvider } from '~/contexts/HeaderContext';
import { useAnalytics } from '~/hooks/useAnalytics';
import { useSubmitGameSession } from '~/hooks/useGameSession';
import { apiClient } from '~/lib/api';
import { clearTokens, getAccessToken, isAuthenticated, isExpired, setTokensFromCallback } from '~/lib/auth';
import { resolveDomain } from '~/lib/domain';
import type { PublicEpisodeInfo, PublicLandingPage } from '~/types';
import { PublicLandingPageSchema } from '~/types/schemas';

// =============================================================================
// Path Parsing — extract composite slug and sub-route from URL
// =============================================================================

const KNOWN_SUBROUTES = new Set(['auth', 'episodes', 'score', 'offer']);

interface ParsedPath {
  slug: string;
  subRoute: string;
  episodeId?: string;
}

function parsePath(pathname: string): ParsedPath {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return { slug: '', subRoute: 'index' };

  const last = segments[segments.length - 1];
  const secondToLast = segments.length >= 2 ? segments[segments.length - 2] : '';

  // /slug.../game/:episodeId
  if (secondToLast === 'game' && segments.length >= 3) {
    return {
      slug: segments.slice(0, -2).join('/'),
      subRoute: 'game',
      episodeId: last,
    };
  }

  // /slug.../auth/callback | /slug.../auth/reset-password
  if (secondToLast === 'auth' && (last === 'callback' || last === 'reset-password') && segments.length >= 3) {
    return {
      slug: segments.slice(0, -2).join('/'),
      subRoute: `auth/${last}`,
    };
  }

  // /slug.../auth | /slug.../episodes | /slug.../score | /slug.../offer
  if (KNOWN_SUBROUTES.has(last) && segments.length > 1) {
    return {
      slug: segments.slice(0, -1).join('/'),
      subRoute: last,
    };
  }

  // Entire path is the slug (index route)
  return { slug: segments.join('/'), subRoute: 'index' };
}

// =============================================================================
// Track which slugs have already shown their branded transition
// =============================================================================

const transitionedSlugs = new Set<string>();

// =============================================================================
// Route Definition
// =============================================================================

export const Route = createFileRoute('/$')({
  beforeLoad: async ({ location, context }) => {
    // SSR guard: window is not available on the server
    // In SPA mode, skip data fetching during SSR - client will handle it
    if (typeof window === 'undefined') {
      // Return minimal context for SSR, client will fetch data
      const { slug, subRoute, episodeId } = parsePath(location.pathname);
      return { slug, subRoute, episodeId };
    }

    const { type, value } = resolveDomain(
      window.location.hostname,
      window.location.pathname,
    );

    // ── Custom domain handling (existing behavior) ──
    if (type === 'custom_domain') {
      const slug = value;
      const path = location.pathname.replace(/^\//, '');

      if (path === 'auth') {
        throw redirect({ to: '/$slug/auth', params: { slug } });
      }
      if (path === 'episodes') {
        throw redirect({ to: '/$slug/episodes', params: { slug } });
      }
      if (path.startsWith('game/')) {
        const episodeId = path.slice(5);
        if (episodeId) {
          throw redirect({
            to: '/$slug/game/$episodeId',
            params: { slug, episodeId },
          });
        }
      }
      if (path === 'score') {
        const searchParams = new URLSearchParams(location.searchStr);
        throw redirect({
          to: '/$slug/score',
          params: { slug },
          search: {
            score: Number(searchParams.get('score') || 0),
            totalQuestions: Number(searchParams.get('totalQuestions') || 1),
          },
        });
      }
      if (path === 'offer') {
        throw redirect({ to: '/$slug/offer', params: { slug } });
      }
      throw redirect({ to: '/$slug', params: { slug } });
    }

    // ── Primary host: composite slug paths ──
    const { slug, subRoute, episodeId } = parsePath(location.pathname);
    if (!slug) return {};

    const landingPage: PublicLandingPage =
      await context.queryClient.ensureQueryData({
        queryKey: ['landingPage', slug],
        queryFn: async () => {
          const data = await apiClient<unknown>(
            `/v1/public/landing-pages/by-slug?slug=${encodeURIComponent(slug)}`,
          );
          return PublicLandingPageSchema.parse(data) as PublicLandingPage;
        },
        staleTime: 1000 * 60 * 10,
      });

    return { slug, landingPage, subRoute, episodeId };
  },
  component: SplatLayout,
  pendingComponent: () => <YakSplashScreen />,
  errorComponent: ({ error, reset }) => (
    <ErrorState
      message={error?.message || 'Failed to load this page. Please try again.'}
      onRetry={reset}
    />
  ),
});

// =============================================================================
// Layout — splash → transition → content with internal sub-routing
// =============================================================================

interface SplatViewProps {
  slug: string;
  landingPage: PublicLandingPage;
  onNavigate: (view: string, episodeId?: string) => void;
}

function SplatLayout() {
  // Track client mount to avoid hydration mismatch
  // SSR renders YakSplashScreen, client hydrates with same, then switches to actual content
  const [isMounted, setIsMounted] = useState(false);

  const context = Route.useRouteContext() as {
    slug?: string;
    landingPage?: PublicLandingPage;
    subRoute?: string;
    episodeId?: string;
  };

  const { slug, landingPage: contextLandingPage } = context;

  // Fetch landing page data client-side if not available from route context (SSR case)
  const { data: fetchedLandingPage, isLoading, error } = useQuery({
    queryKey: ['landingPage', slug],
    queryFn: async () => {
      const data = await apiClient<unknown>(
        `/v1/public/landing-pages/by-slug?slug=${encodeURIComponent(slug!)}`,
      );
      return PublicLandingPageSchema.parse(data) as PublicLandingPage;
    },
    enabled: isMounted && !!slug && !contextLandingPage,
    staleTime: 1000 * 60 * 10,
  });

  const landingPage = contextLandingPage || fetchedLandingPage;

  // Sub-route state (allows internal navigation without full page reload)
  const [view, setView] = useState(context.subRoute ?? 'index');
  const [episodeId, setEpisodeId] = useState(context.episodeId);
  const [scoreData, setScoreData] = useState<{
    score: number;
    totalQuestions: number;
  } | null>(null);

  // Transition only on first visit per slug
  // Initialize to false for SSR consistency, then check on client mount
  const [showTransition, setShowTransition] = useState(false);

  // Mark as mounted after hydration completes
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Check transition state on client mount only (avoids hydration mismatch)
  useEffect(() => {
    if (isMounted && slug && !transitionedSlugs.has(slug)) {
      setShowTransition(true);
    }
  }, [isMounted, slug]);

  const handleTransitionComplete = useCallback(() => {
    if (slug) transitionedSlugs.add(slug);
    setShowTransition(false);
  }, [slug]);

  // Safety timeout for transition
  useEffect(() => {
    if (!showTransition) return;
    const timer = setTimeout(() => {
      if (slug) transitionedSlugs.add(slug);
      setShowTransition(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [showTransition, slug]);

  // Navigate between views (updates URL + state)
  const navigateTo = useCallback(
    (newView: string, newEpisodeId?: string) => {
      let path: string;
      if (newView === 'index') {
        path = `/${slug}`;
      } else if (newView === 'game' && newEpisodeId) {
        path = `/${slug}/game/${newEpisodeId}`;
      } else {
        path = `/${slug}/${newView}`;
      }
      window.history.pushState({}, '', path);
      setView(newView);
      if (newEpisodeId) setEpisodeId(newEpisodeId);
    },
    [slug],
  );

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const parsed = parsePath(window.location.pathname);
      setView(parsed.subRoute);
      setEpisodeId(parsed.episodeId);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // ── Guards ──

  // Always render YakSplashScreen during SSR and initial client render
  // This prevents hydration mismatch since SSR prerenders "/" but client may load "/slug/..."
  if (!isMounted) {
    return <YakSplashScreen />;
  }

  if (!slug) {
    return <ErrorState message="Landing page not found" />;
  }

  if (isLoading) {
    return <YakSplashScreen />;
  }

  if (error || !landingPage) {
    return <ErrorState message={error?.message || "Landing page data unavailable"} />;
  }

  // ── Transition screen ──

  if (showTransition) {
    return (
      <BrandedTransitionScreen
        branding={landingPage.branding}
        duration={1000}
        onTransitionComplete={handleTransitionComplete}
      />
    );
  }

  // ── Sub-route rendering ──

  const renderView = () => {
    switch (view) {
      case 'auth/callback':
        return (
          <SplatAuthCallbackView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
          />
        );
      case 'auth/reset-password':
        return (
          <SplatResetPasswordView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
          />
        );
      case 'auth':
        return (
          <SplatAuthView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
          />
        );
      case 'episodes':
        return (
          <SplatEpisodesView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
          />
        );
      case 'game':
        return (
          <SplatGameView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
            episodeId={episodeId!}
            onScoreData={setScoreData}
          />
        );
      case 'score':
        return (
          <SplatScoreView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
            scoreData={scoreData}
          />
        );
      case 'offer':
        return (
          <SplatOfferView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
          />
        );
      case 'index':
      default:
        return (
          <SmartRedirect
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
          />
        );
    }
  };

  return (
    <AuthProvider slug={slug}>
      <HeaderProvider>
        {isAuthenticated() && slug && (
          <AppHeader slug={slug} />
        )}
        {renderView()}
      </HeaderProvider>
    </AuthProvider>
  );
}

// =============================================================================
// Smart Redirect — check auth, route to auth / episodes / game
// =============================================================================

function SmartRedirect({ slug, landingPage, onNavigate }: SplatViewProps) {
  useEffect(() => {
    if (isExpired()) clearTokens();

    if (!isAuthenticated()) {
      onNavigate('auth');
      return;
    }

    if (landingPage.targetType === 'show') {
      onNavigate('episodes');
    } else {
      onNavigate('game', landingPage.targetId);
    }
  }, [landingPage, onNavigate, slug]);

  return <YakSplashScreen />;
}

// =============================================================================
// Auth View
// =============================================================================

function SplatAuthView({ slug, landingPage, onNavigate }: SplatViewProps) {
  const { trackEvent } = useAnalytics({
    slug,
    landingPageId: landingPage.id,
  });

  // Already authenticated → skip to content
  useEffect(() => {
    if (isAuthenticated()) {
      navigateAfterAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateAfterAuth = useCallback(() => {
    if (landingPage.targetType === 'show') {
      onNavigate('episodes');
    } else {
      onNavigate('game', landingPage.targetId);
    }
  }, [landingPage, onNavigate]);

  const handleAuthSuccess = useCallback(
    (mode: 'register' | 'login') => {
      if (mode === 'register') trackEvent('registration_complete');
      else if (mode === 'login') trackEvent('login_complete');
      navigateAfterAuth();
    },
    [trackEvent, navigateAfterAuth],
  );

  return (
    <LandingView landingPage={landingPage}>
      <AuthForms slug={slug} onSuccess={handleAuthSuccess} />
    </LandingView>
  );
}

// =============================================================================
// Auth Callback View — handles social login token extraction
// =============================================================================

function SplatAuthCallbackView({ slug, onNavigate }: SplatViewProps) {
  const [needsName, setNeedsName] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (!hash) {
      onNavigate('auth');
      return;
    }

    const params = new URLSearchParams(hash);
    const token = params.get('token');
    const refreshToken = params.get('refreshToken') || '';
    const userId = params.get('userId');
    const needsNameParam = params.get('needsName') === 'true';

    if (!token || !userId) {
      onNavigate('auth');
      return;
    }

    setTokensFromCallback({ accessToken: token, refreshToken, userId, slug });
    window.history.replaceState(null, '', window.location.pathname);

    if (needsNameParam) {
      setNeedsName(true);
    } else {
      onNavigate('index');
    }
  }, [onNavigate, slug]);

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || saving) return;

    setSaving(true);
    try {
      const token = getAccessToken();
      await apiClient('/v1/public/profile/display-name', {
        method: 'PATCH',
        body: { displayName: displayName.trim() },
        token,
      });
    } catch {
      // Continue even if name save fails
    }
    onNavigate('index');
  };

  if (needsName) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <h2 className="text-center text-xl font-semibold">
            What should we call you?
          </h2>
          <form onSubmit={handleNameSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              className="w-full rounded-md border px-3 py-2 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              disabled={saving || !displayName.trim()}
              className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white min-h-[44px] hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Continue'}
            </button>
            <button
              type="button"
              onClick={() => onNavigate('index')}
              className="w-full text-sm text-gray-500 hover:underline min-h-[44px]"
            >
              Skip
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-gray-500">Signing in...</p>
    </div>
  );
}

// =============================================================================
// Reset Password View
// =============================================================================

function SplatResetPasswordView({ slug, landingPage, onNavigate }: SplatViewProps) {
  const [status, setStatus] = useState<'form' | 'success' | 'expired'>('form');
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  const renderContent = () => {
    if (!token) {
      return (
        <div className="w-full max-w-sm mx-auto text-center space-y-4">
          <p className="text-sm text-red-600">Invalid reset link. No token provided.</p>
          <button
            type="button"
            onClick={() => onNavigate('auth')}
            className="text-sm font-medium hover:underline"
            style={{ color: landingPage.branding.primaryColor }}
          >
            Back to sign in
          </button>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="w-full max-w-sm mx-auto text-center space-y-4">
          <h2 className="text-xl font-semibold" style={{ color: landingPage.branding.primaryColor }}>
            Password Reset Successfully
          </h2>
          <p className="text-sm text-gray-600">Your password has been updated. You can now sign in with your new password.</p>
          <button
            type="button"
            onClick={() => onNavigate('auth')}
            className="inline-block rounded-md px-6 py-2 text-sm font-medium text-white min-h-[44px] transition-colors duration-150"
            style={{ backgroundColor: landingPage.branding.primaryColor }}
          >
            Sign in
          </button>
        </div>
      );
    }

    if (status === 'expired') {
      return (
        <div className="w-full max-w-sm mx-auto text-center space-y-4">
          <h2 className="text-xl font-semibold" style={{ color: landingPage.branding.primaryColor }}>
            Link Expired
          </h2>
          <p className="text-sm text-gray-600">This reset link has expired. Please request a new one.</p>
          <button
            type="button"
            onClick={() => onNavigate('auth')}
            className="text-sm font-medium hover:underline"
            style={{ color: landingPage.branding.primaryColor }}
          >
            Back to sign in
          </button>
        </div>
      );
    }

    return (
      <ResetPasswordForm
        token={token}
        onSuccess={() => setStatus('success')}
        onExpired={() => setStatus('expired')}
      />
    );
  };

  return (
    <LandingView landingPage={landingPage}>
      {renderContent()}
    </LandingView>
  );
}

// =============================================================================
// Episodes View
// =============================================================================

function SplatEpisodesView({ slug, landingPage, onNavigate }: SplatViewProps) {
  const { trackEvent } = useAnalytics({
    slug,
    landingPageId: landingPage.id,
  });

  useEffect(() => {
    if (isExpired()) clearTokens();
    if (!isAuthenticated()) {
      onNavigate('auth');
      return;
    }
    trackEvent('view');
  }, [trackEvent, onNavigate]);

  const seasonMap = useMemo(() => {
    if (!landingPage.episodes) return new Map<string, PublicEpisodeInfo[]>();
    const map = new Map<string, PublicEpisodeInfo[]>();
    for (const ep of landingPage.episodes) {
      const season = ep.seasonName ?? 'Season 1';
      const list = map.get(season) ?? [];
      list.push(ep);
      map.set(season, list);
    }
    return map;
  }, [landingPage.episodes]);

  const seasonNames = useMemo(() => Array.from(seasonMap.keys()), [seasonMap]);
  const [activeSeason, setActiveSeason] = useState('');

  useEffect(() => {
    if (seasonNames.length > 0 && !activeSeason) {
      setActiveSeason(seasonNames[0]);
    }
  }, [seasonNames, activeSeason]);

  const activeEpisodes = useMemo(
    () => seasonMap.get(activeSeason) ?? [],
    [seasonMap, activeSeason],
  );

  return (
    <LandingView landingPage={landingPage}>
      <div className="flex flex-col gap-4">
        <SeasonSelector
          seasons={seasonNames}
          activeSeason={activeSeason}
          onSelectSeason={setActiveSeason}
          branding={landingPage.branding}
        />
        <EpisodeList
          episodes={activeEpisodes}
          onSelectEpisode={(id) => onNavigate('game', id)}
          branding={landingPage.branding}
        />
      </div>
    </LandingView>
  );
}

// =============================================================================
// Game View
// =============================================================================

function SplatGameView({
  slug,
  landingPage,
  episodeId,
  onNavigate,
  onScoreData,
}: SplatViewProps & {
  episodeId: string;
  onScoreData: (data: { score: number; totalQuestions: number }) => void;
}) {
  const submitSession = useSubmitGameSession();
  const gameStartTimeRef = useRef<string>(new Date().toISOString());

  useEffect(() => {
    if (isExpired()) clearTokens();
    if (!isAuthenticated()) {
      onNavigate('auth');
    }
  }, [onNavigate]);

  const handleGameComplete = useCallback(
    (data?: { score: number; totalQuestions: number }) => {
      // Submit game session if user is authenticated (fire-and-forget)
      if (data && isAuthenticated()) {
        const pointsPerCorrectAnswer = landingPage.gameOptions.pointsPerCorrectAnswer;
        submitSession.mutate({
          episodeId,
          landingPageId: landingPage.id,
          organizationId: landingPage.organizationId,
          score: data.score,
          totalQuestions: data.totalQuestions,
          correctAnswers: pointsPerCorrectAnswer > 0 ? Math.round(data.score / pointsPerCorrectAnswer) : 0,
          pointsPerCorrectAnswer,
          pointsEarned: data.score,
          startedAt: gameStartTimeRef.current,
        });
      }

      if (data) {
        onScoreData(data);
        onNavigate('score');
      } else {
        onNavigate('offer');
      }
    },
    [onNavigate, onScoreData, submitSession, episodeId, landingPage],
  );

  return (
    <div className="h-screen w-screen overflow-hidden">
      <GamePlayer
        slug={slug}
        episodeId={episodeId}
        gameOptions={landingPage.gameOptions}
        onGameComplete={handleGameComplete}
        className="h-full w-full"
      />
    </div>
  );
}

// =============================================================================
// Score View
// =============================================================================

function SplatScoreView({
  landingPage,
  onNavigate,
  scoreData,
}: SplatViewProps & {
  scoreData: { score: number; totalQuestions: number } | null;
}) {
  const score = scoreData?.score ?? 0;
  const totalQuestions = scoreData?.totalQuestions ?? 1;
  const ptsPerAnswer = landingPage.gameOptions.pointsPerCorrectAnswer;
  const maxPoints = totalQuestions * ptsPerAnswer;
  const displayScore = score;
  const percentage =
    maxPoints > 0 ? Math.round((displayScore / maxPoints) * 100) : 0;

  useEffect(() => {
    const timer = setTimeout(() => onNavigate('offer'), 2000);
    return () => clearTimeout(timer);
  }, [onNavigate]);

  return (
    <div
      className="flex h-screen w-screen items-center justify-center p-2 sm:p-4 md:p-8 lg:p-16"
      style={{
        backgroundImage:
          'url(https://s3.amazonaws.com/yak.assets/52xP82c.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="flex h-full w-full max-w-[95vw] flex-col items-center justify-center overflow-hidden rounded-xl bg-white/95 p-4 shadow-2xl md:aspect-video md:h-auto md:max-w-7xl md:rounded-2xl md:p-12">
        <div className="flex flex-col items-center gap-4 text-center md:gap-8">
          {landingPage.branding.logoUrl && (
            <img
              src={landingPage.branding.logoUrl}
              alt="Brand logo"
              className="h-12 object-contain md:h-20"
            />
          )}
          <div className="flex flex-col gap-2 md:gap-4">
            <h1
              className="text-4xl font-bold md:text-6xl"
              style={{ color: landingPage.branding.primaryColor }}
            >
              {displayScore}/{maxPoints}
            </h1>
            <p className="text-sm font-medium text-gray-500 md:text-lg">
              points earned out of {maxPoints} possible
            </p>
            <p
              className="text-2xl font-semibold md:text-4xl"
              style={{ color: landingPage.branding.secondaryColor }}
            >
              {percentage}%
            </p>
          </div>
          <p className="text-lg font-medium text-gray-700 md:text-2xl">
            Thanks for playing!
          </p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Offer View
// =============================================================================

function SplatOfferView({ slug, landingPage, onNavigate }: SplatViewProps) {
  const { trackEvent } = useAnalytics({
    slug,
    landingPageId: landingPage.id,
  });

  const handlePlayAgain = useCallback(() => {
    if (landingPage.targetType === 'show') {
      onNavigate('episodes');
    } else {
      onNavigate('game', landingPage.targetId);
    }
  }, [landingPage, onNavigate]);

  return (
    <OfferPage
      offerContent={landingPage.offerContent}
      branding={landingPage.branding}
      forwardUrl={landingPage.forwardUrl}
      title={landingPage.title}
      onForwardClick={() => trackEvent('offer_click')}
      onOfferView={() => trackEvent('offer_view')}
      targetType={landingPage.targetType}
      onPlayAgain={handlePlayAgain}
      showYakOffer={landingPage.showYakOffer}
    />
  );
}
